/**
 * PipelineSimulation.js — Production Pipeline Simulation Runner UI.
 * Route: /simulation
 *
 * One-button E2E walkthrough that fires every module in the digital platform
 * against the live backend and streams the transcript step-by-step. Use this
 * to prove the system is doing what it was built to do before flipping
 * sandbox mode off.
 */
import React, { useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#020A1F"; const CYAN = "#00CFCF"; const CHROME = "#A0A0A0"; const INK = "#F5F5F7";
const shell = { minHeight: "100dvh", background: NAVY, color: INK, padding: "clamp(16px,3vw,32px)", fontFamily: "'Inter', system-ui, sans-serif", boxSizing: "border-box" };
const card = { background: "rgba(255,255,255,0.03)", border: `1px solid ${CHROME}33`, borderRadius: 12, padding: "clamp(14px,2.5vw,22px)", marginBottom: 16 };
const btn = { background: CYAN, color: NAVY, border: 0, padding: "14px 28px", fontWeight: 700, borderRadius: 8, cursor: "pointer", fontSize: 14, letterSpacing: "0.05em", textTransform: "uppercase" };
const btnGhost = { ...btn, background: "transparent", color: INK, border: `1px solid ${CHROME}55` };
const input = { width: "100%", background: "rgba(0,0,0,0.35)", border: `1px solid ${CHROME}44`, color: INK, padding: "10px 12px", borderRadius: 6, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" };

export default function PipelineSimulation() {
  const [address, setAddress] = useState("5960 Siltstone Lane, Unit 932, Fort Worth TX 76137");
  const [signer, setSigner] = useState("nivoomforward@gmail.com");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);

  const run = async () => {
    setRunning(true); setError(null); setResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/simulation/run`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ property_address: address, signer_email: signer }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Simulation failed");
      setResult(d);
    } catch (e) { setError(e.message); }
    setRunning(false);
  };

  const verifyAndLock = async () => {
    setVerifyResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/system/verify-and-lock`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ verified_by_email: signer }),
      });
      const d = await r.json();
      setVerifyResult({ ok: r.ok, data: d });
    } catch (e) { setVerifyResult({ ok: false, data: { detail: e.message } }); }
  };

  return (
    <div style={shell} data-testid="simulation-shell">
      <div style={{ ...card, borderColor: `${CYAN}44` }}>
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 6 }}>
          MOOVIN FORWARD LLC · PRODUCTION PIPELINE SIMULATION
        </div>
        <h1 style={{ margin: 0, fontSize: "clamp(20px, 3vw, 28px)" }}>
          Watch the digital platform execute end-to-end
        </h1>
        <div style={{ color: CHROME, marginTop: 6, fontSize: 13 }}>
          One button fires all 11 production modules against the live backend. Every step returns a real
          Mongo write, real service response, real hash chain. When you see 11/11 green, hit
          Verify &amp; Lock to permanently flip sandbox off.
        </div>
      </div>

      <div style={card}>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
          <label>
            <div style={{ fontSize: 12, color: CHROME, marginBottom: 4 }}>Simulation property</div>
            <input style={input} value={address} onChange={e => setAddress(e.target.value)} data-testid="sim-address" />
          </label>
          <label>
            <div style={{ fontSize: 12, color: CHROME, marginBottom: 4 }}>Signer email</div>
            <input style={input} value={signer} onChange={e => setSigner(e.target.value)} data-testid="sim-signer" />
          </label>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button style={{ ...btn, opacity: running ? 0.6 : 1 }} onClick={run} disabled={running} data-testid="sim-run-btn">
            {running ? "Running pipeline…" : "▶ Run Full Pipeline"}
          </button>
          {result && result.steps_ok === result.steps_total && (
            <button style={btnGhost} onClick={verifyAndLock} data-testid="sim-verify-btn">
              🔒 Verify &amp; Lock Sandbox
            </button>
          )}
        </div>
        {error && <div style={{ marginTop: 12, color: "#f04040" }} data-testid="sim-error">{error}</div>}
      </div>

      {result && (
        <div style={{ ...card, borderColor: result.steps_ok === result.steps_total ? CYAN : "#f5a942" }} data-testid="sim-summary">
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 6 }}>SIMULATION RESULT</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>
            {result.steps_ok} / {result.steps_total} steps
            {result.steps_ok === result.steps_total ? " ✓ ALL GREEN" : " ⚠ REVIEW"}
          </div>
          <div style={{ fontSize: 12, color: CHROME, marginTop: 4 }}>
            simulation_id: <code style={{ color: CYAN }}>{result.simulation_id}</code> · project_id: <code style={{ color: CYAN }}>{result.project_id}</code> · activation_id: <code style={{ color: CYAN }}>{result.activation_id}</code>
          </div>
        </div>
      )}

      {result && result.transcript.map(step => (
        <div key={step.step} style={{
          ...card,
          borderColor: step.status === "ok" ? `${CYAN}44` : "#f04040",
          background: step.status === "ok" ? "rgba(0,207,207,0.04)" : "rgba(240,64,64,0.08)",
        }} data-testid={`sim-step-${step.step}`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 10, color: step.status === "ok" ? CYAN : "#f04040", letterSpacing: "0.2em" }}>
                STEP {step.step} · {step.status === "ok" ? "GREEN" : "FAIL"}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{step.name}</div>
            </div>
            <div style={{ fontSize: 20 }}>{step.status === "ok" ? "✓" : "✗"}</div>
          </div>
          <div style={{ marginTop: 8, fontSize: 13, color: INK }}>{step.detail}</div>
          {step.payload && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 11, color: CHROME, cursor: "pointer" }}>Show raw response</summary>
              <pre style={{ marginTop: 8, fontSize: 11, background: "rgba(0,0,0,0.35)", padding: 10, borderRadius: 6, overflowX: "auto" }}>
                {JSON.stringify(step.payload, null, 2)}
              </pre>
            </details>
          )}
        </div>
      ))}

      {verifyResult && (
        <div style={{
          ...card,
          borderColor: verifyResult.ok ? CYAN : "#f04040",
          background: verifyResult.ok ? "rgba(0,207,207,0.08)" : "rgba(240,64,64,0.1)",
        }} data-testid="sim-verify-result">
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: verifyResult.ok ? CYAN : "#f04040" }}>
            {verifyResult.ok ? "🔒 SANDBOX VERIFIED + LOCKED" : "⚠ VERIFICATION BLOCKED"}
          </div>
          <pre style={{ marginTop: 10, fontSize: 11, background: "rgba(0,0,0,0.35)", padding: 10, borderRadius: 6, overflowX: "auto" }}>
            {JSON.stringify(verifyResult.data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
